import type { ChatContext, CliAction } from '@/store/chat-types'
import type { CogoElement, LoadedApp } from '@/features/page-editor/store/types'
import { useStudioStore } from '@/store/studio-store'
import type { SingleActionIntentResponse } from './mock-chat-service'
import { readCogoDocumentWorkspaceFromProject } from '@/features/document-workspace/model/document-operation'
import {
  resolveStudioTargetTerm,
  type StudioTargetResolution,
} from './studio-terminology-routing'
import { getLocale } from '@/pages/cogo-marketing/i18n/locale-store'
import type { LocaleCode } from '@/pages/cogo-marketing/i18n/locales'
import {
  specIntentClarificationText,
  type SpecIntentClarificationKey,
  type SpecIntentClarificationParams,
} from './live-cogo-spec-intent-copy'
import { hasStudioChatMutationIntent } from './studio-chat-mutation-intent'

const UI_WORDS = /(text|label|copy|headline|title|button|component|element|문구|텍스트|라벨|제목|버튼|컴포넌트|요소)/i
const VARIABLE_WORDS = /(variable|state|local\s*state|변수|상태)/i
const DATASOURCE_WORDS = /(datasource|data\s*source|api|endpoint|데이터소스|데이터\s*소스|엔드포인트)/i
const FLOW_WORDS = /(actionflow|action\s*flow|flow|액션플로우|플로우)/i
const EVENT_BINDING_WORDS = /(event\s*binding|event|handler|이벤트\s*바인딩|이벤트|핸들러)/i
const PAGE_WORDS = /\b(page|screen|route|view)\b|페이지|스크린|라우트|새\s*화면|새로운\s*화면|화면\s*(?:추가|생성|만들)/i
const UI_MUTATION_WORDS = /(text|label|copy|headline|button|component|element|문구|텍스트|라벨|버튼|컴포넌트|요소)/i
const BROAD_APP_GENERATION_WORDS =
  /\b(new\s+independent\s+studio\s+project|new\s+independent\s+project|first-time\s+cogo\s+user|never\s+used\s+cogo|build\s+(?:a|an|the)?\s*[^.]*\b(?:app|application|dashboard|tracker|board)|create\s+(?:a|an|the)?\s*[^.]*\b(?:app|application|dashboard|tracker|board))\b/i

export function detectLiveCogoSpecIntent(
  content: string,
  locale: LocaleCode = getLocale(),
): SingleActionIntentResponse | null {
  const store = useStudioStore.getState()
  const project = store.currentProject as LoadedApp | null
  if (!project || !hasStudioChatMutationIntent(content)) return null
  if (BROAD_APP_GENERATION_WORDS.test(content)) return null

  const documentPageIntent = buildDocumentPageIntent(
    content,
    project,
    locale,
    store.chatContext.surfaceId,
  )
  if (documentPageIntent) return documentPageIntent

  const pageIntent = buildPageIntent(content, project)
  if (pageIntent) return pageIntent

  const blockName = resolvePrimaryBlockName(project, store.currentPageId)
  if (!blockName) return null
  const contextualBlockName = store.chatContext.selectedBlockName ?? blockName

  // ActionFlow commands can legitimately mention `state`, `setState`, or a
  // datasource while describing their steps. Route on the explicit Flow noun
  // first so the variable/data parsers cannot steal the higher-level intent.
  if (FLOW_WORDS.test(content)) {
    const flowIntent = buildFlowIntent(content, store.chatContext, locale)
    if (flowIntent) return flowIntent
  }

  const variableIntent = buildVariableIntent(content, contextualBlockName, locale)
  if (variableIntent) return variableIntent

  const datasourceIntent = buildDatasourceIntent(content, contextualBlockName, locale)
  if (datasourceIntent) return datasourceIntent

  const eventBindingIntent = buildEventBindingIntent(content, store.chatContext, locale)
  if (eventBindingIntent) return eventBindingIntent

  const targetResolution = resolveUiTarget(content)
  if (
    UI_WORDS.test(content) ||
    extractQuotedText(content) ||
    (targetResolution && targetResolution.status !== 'undefined')
  ) {
    return buildUiIntent(
      content,
      contextualBlockName,
      store.chatContext,
      targetResolution,
      locale,
      project,
      store.currentPageId,
    )
  }

  return null
}

function buildDocumentPageIntent(
  content: string,
  project: LoadedApp,
  locale: LocaleCode,
  surfaceId?: string,
): SingleActionIntentResponse | null {
  if (surfaceId !== 'document-workspace') return null
  if (!readCogoDocumentWorkspaceFromProject(project)) return null
  if (!PAGE_WORDS.test(content)) return null
  if (!/(add|create|insert|추가|생성|삽입)/i.test(content)) return null

  const title = extractDocumentPageTitle(content)
  if (!title) {
    return clarification('document-page-title', locale)
  }

  return {
    text: specIntentClarificationText('document-page-prepared', locale, { title }),
    actions: [{
      type: 'cogo.studio.document.create_page',
      payload: { title, kind: 'page' },
    }],
  }
}

function buildPageIntent(content: string, project: LoadedApp): SingleActionIntentResponse | null {
  if (!PAGE_WORDS.test(content)) return null
  if (UI_MUTATION_WORDS.test(content)) return null
  if (FLOW_WORDS.test(content) || DATASOURCE_WORDS.test(content) || VARIABLE_WORDS.test(content)) return null
  if (!/(add|create|insert|show|display|추가|생성|삽입|표시|보여)/i.test(content)) return null

  const title = extractPageTitle(content)
  if (!title) return null
  const baseId = slugify(title) || 'generatedPage'
  const pageId = uniqueId(baseId, new Set((project.manifest?.pages ?? []).map((page) => page.id)))
  const blockName = uniqueId(`${pageId}Screen`, new Set(Object.keys(project.blocks ?? {})))
  const route = `/${kebabCase(pageId)}`

  return {
    text: `Added "${title}" as a new screen and opened the app.`,
    actions: [
      patchAction(
        [
          {
            op: 'add',
            path: `/blocks/${escapePointerPart(blockName)}`,
            value: buildPageBlock(pageId, title),
          },
          {
            op: 'add',
            path: '/manifest/pages/-',
            value: {
              id: pageId,
              route,
              title,
              name: title,
              blocks: [blockName],
              layout: 'stack',
              blockLayout: {},
            },
          },
        ],
        { selectPageId: pageId },
      ),
    ],
  }
}

function buildUiIntent(
  content: string,
  blockName: string,
  context: ChatContext,
  targetResolution: StudioTargetResolution | null,
  locale: LocaleCode,
  project: LoadedApp,
  currentPageId?: string | null,
): SingleActionIntentResponse | null {
  const replacement = extractExactTextReplacement(content)
  let text = replacement?.replacementText ?? extractTextContent(content)
  let selectedElementId = context.selectedElementId
  let selectedBlockName = context.selectedBlockName ?? blockName

  if (/(remove|delete|삭제|제거)/i.test(content)) {
    if (!selectedElementId) {
      return clarification('remove-element', locale)
    }
    return {
      text: 'Prepared the selected element for removal through Studio WebMCP.',
      actions: [{
        type: 'cogo.studio.removeElement',
        payload: { blockName: selectedBlockName, elementId: selectedElementId },
      }],
    }
  }

  if (/(update|change|replace|변경|수정|교체)/i.test(content)) {
    if (!selectedElementId && currentPageId) {
      const exactTarget = replacement
        ? resolveExactPageTextTarget(project, currentPageId, replacement.sourceText)
        : resolveMentionedPageTextTarget(project, currentPageId, content)
      if (exactTarget) {
        selectedElementId = exactTarget.elementId
        selectedBlockName = exactTarget.blockName
        text ??= extractReplacementTextFollowingSource(content, exactTarget.sourceText)
      }
    }
    if (!selectedElementId) {
      return clarification('update-element', locale)
    }
    if (!text) return buildTextClarificationIntent(locale, 'update')
    return {
      text: `Prepared the selected element text change to "${text}" through Studio WebMCP.`,
      actions: [{
        type: 'cogo.studio.updateElement',
        payload: {
          blockName: selectedBlockName,
          elementId: selectedElementId,
          changes: { text },
        },
      }],
    }
  }

  if (!text) return buildTextClarificationIntent(locale, 'add')
  if (
    targetResolution?.status === 'ambiguous' &&
    !selectedElementId
  ) {
    return clarification('component-type', locale, {
      term: targetResolution.rawTerm,
    })
  }
  const componentType =
    targetResolution?.elementType === 'button' || /button|버튼/i.test(content)
      ? 'button'
      : 'text'
  return {
    text: `Prepared a ${componentType} with the exact copy "${text}" through Studio WebMCP.`,
    actions: [{
      type: 'cogo.studio.addElement',
      payload: {
        blockName: selectedBlockName,
        component_type: componentType,
        ...(componentType === 'button' ? { label: text, text } : { text }),
      },
    }],
  }
}

interface ExactPageTextTarget {
  blockName: string
  elementId: string
  sourceText: string
}

function resolveExactPageTextTarget(
  project: LoadedApp,
  currentPageId: string,
  sourceText: string,
): ExactPageTextTarget | null {
  const page = project.manifest.pages?.find((candidate) => candidate.id === currentPageId)
  if (!page) return null

  const matches: ExactPageTextTarget[] = []
  for (const blockName of page.blocks ?? []) {
    const root = project.blocks[blockName]?.ui?.root
    if (!root) continue
    collectExactTextTargets(root, blockName, matches, (text) => text === sourceText)
    if (matches.length > 1) return null
  }

  return matches.length === 1 ? matches[0] : null
}

function resolveMentionedPageTextTarget(
  project: LoadedApp,
  currentPageId: string,
  content: string,
): ExactPageTextTarget | null {
  const page = project.manifest.pages?.find((candidate) => candidate.id === currentPageId)
  if (!page) return null

  const matches: ExactPageTextTarget[] = []
  for (const blockName of page.blocks ?? []) {
    const root = project.blocks[blockName]?.ui?.root
    if (!root) continue
    collectExactTextTargets(root, blockName, matches, (text) => content.includes(text))
    if (matches.length > 1) return null
  }

  return matches.length === 1 ? matches[0] : null
}

function collectExactTextTargets(
  element: CogoElement,
  blockName: string,
  matches: ExactPageTextTarget[],
  acceptsText: (text: string) => boolean,
): void {
  const sourceText = element.text?.trim()
  if (sourceText && !sourceText.startsWith('#{') && acceptsText(sourceText)) {
    matches.push({ blockName, elementId: element.id, sourceText })
  }
  for (const child of element.children ?? []) {
    collectExactTextTargets(child, blockName, matches, acceptsText)
  }
}

function buildVariableIntent(
  content: string,
  blockName: string,
  locale: LocaleCode,
): SingleActionIntentResponse | null {
  if (!VARIABLE_WORDS.test(content)) return null
  const name = extractNameAfter(content, VARIABLE_WORDS)
  if (!name || isGenericCommandTarget(name)) {
    return clarification('variable-name', locale)
  }

  if (/(remove|delete|삭제|제거)/i.test(content)) {
    return {
      text: `Prepared variable "${name}" for removal through Studio WebMCP.`,
      actions: [{
        type: 'cogo.studio.removeVariable',
        payload: { blockName, scope: 'state', name },
      }],
    }
  }

  const type = extractVariableType(content)
  if (!type) {
    return clarification('variable-type', locale, { name })
  }

  if (/(update|change|replace|변경|수정|교체)/i.test(content)) {
    return {
      text: `Prepared variable "${name}" type change to ${type} through Studio WebMCP.`,
      actions: [{
        type: 'cogo.studio.updateVariable',
        payload: { blockName, scope: 'state', name, type },
      }],
    }
  }

  return {
    text: `Prepared "${name}" as ${type} app state through Studio WebMCP.`,
    actions: [{
      type: 'cogo.studio.addVariable',
      payload: { blockName, scope: 'state', name, type },
    }],
  }
}

function buildDatasourceIntent(
  content: string,
  blockName: string,
  locale: LocaleCode,
): SingleActionIntentResponse | null {
  if (!DATASOURCE_WORDS.test(content)) return null
  const name = extractNameAfter(content, DATASOURCE_WORDS)
  if (!name || isGenericCommandTarget(name)) {
    return clarification('datasource-name', locale)
  }

  if (/(remove|delete|삭제|제거)/i.test(content)) {
    return {
      text: `Prepared datasource "${name}" for removal through Studio WebMCP.`,
      actions: [{
        type: 'cogo.studio.removeDatasource',
        payload: { blockName, name },
      }],
    }
  }

  const url = content.match(/https?:\/\/[^\s"'`]+/i)?.[0]
  const method = content.match(/\b(GET|POST|PUT|PATCH|DELETE)\b/i)?.[1]?.toUpperCase()
  const updating = /(update|change|replace|변경|수정|교체)/i.test(content)
  if (updating && !url && !method) {
    return clarification('datasource-url-or-method', locale, { name })
  }
  if (!updating && !url) {
    return clarification('datasource-endpoint', locale, { name })
  }

  if (updating) {
    return {
      text: `Prepared the explicit datasource changes for "${name}" through Studio WebMCP.`,
      actions: [{
        type: 'cogo.studio.updateDatasource',
        payload: {
          blockName,
          name,
          ...(url ? { url } : {}),
          ...(method ? { method } : {}),
        },
      }],
    }
  }

  return {
    text: `Prepared datasource "${name}" with the explicit endpoint through Studio WebMCP.`,
    actions: [{
      type: 'cogo.studio.addDatasource',
      payload: {
        blockName,
        name,
        type: 'rest',
        url,
        ...(method ? { method } : {}),
        ...(!method ? { status: 'needs-configuration' } : {}),
      },
    }],
  }
}

function buildEventBindingIntent(
  content: string,
  context: ChatContext,
  locale: LocaleCode,
): SingleActionIntentResponse | null {
  if (!EVENT_BINDING_WORDS.test(content)) return null
  if (!/(remove|delete|unbind|disconnect|삭제|제거|해제)/i.test(content)) return null

  const blockName = context.selectedBlockName
  const elementId = context.selectedElementId
  if (!blockName || !elementId) {
    return clarification('unbind-element', locale)
  }
  const eventType = extractEventType(content)
  if (!eventType) {
    return clarification('unbind-event-type', locale)
  }

  return {
    text: `Prepared the selected element's ${eventType} binding for removal through Studio WebMCP.`,
    actions: [{
      type: 'cogo.studio.removeEventBinding',
      payload: {
        blockName,
        eventKey: `${elementId}:${eventType}`,
        element: { elementId, eventType },
      },
    }],
  }
}

function buildFlowIntent(
  content: string,
  context: ChatContext,
  locale: LocaleCode,
): SingleActionIntentResponse | null {
  if (!FLOW_WORDS.test(content)) return null
  const flowId = context.flowId

  if (/(rename|이름\s*변경|이름\s*바꾸|명칭\s*변경)/i.test(content)) {
    if (!flowId) {
      return clarification('flow-rename-select', locale)
    }
    const newName = extractRenameTarget(content)
    if (!newName || isGenericCommandTarget(newName)) {
      return clarification('flow-rename-name', locale)
    }
    return {
      text: `Prepared the selected ActionFlow rename to "${newName}" through Studio WebMCP.`,
      actions: [{
        type: 'cogo.studio.renameFlow',
        payload: {
          flowId,
          ...(context.selectedBlockName ? { blockName: context.selectedBlockName } : {}),
          newName,
        },
      }],
      targetMode: 'flow',
    }
  }

  if (/(remove|delete|삭제|제거)/i.test(content)) {
    if (!flowId) {
      return clarification('flow-delete-select', locale)
    }
    return {
      text: 'Prepared the selected ActionFlow for deletion through Studio WebMCP.',
      actions: [{
        type: 'cogo.studio.deleteFlow',
        payload: {
          flowId,
          ...(context.selectedBlockName ? { blockName: context.selectedBlockName } : {}),
        },
      }],
      targetMode: 'flow',
    }
  }

  const extractedName = extractExplicitFlowName(content) ?? extractNameAfter(content, FLOW_WORDS)
  if (!extractedName || isGenericCommandTarget(extractedName)) {
    return clarification('flow-create-name', locale)
  }
  return {
    text: `Prepared the "${extractedName}" behavior through Studio WebMCP.`,
    actions: [{
      type: 'cogo.studio.addFlow',
      payload: { name: extractedName, preserveSurface: true },
    }],
    targetMode: 'flow',
  }
}

function extractExplicitFlowName(content: string): string | null {
  const match = content.match(
    /(?:actionflow|action\s*flow|flow|액션플로우|플로우)\s+(?:(?:named|called|이름(?:은|을|를)?)\s+)?["“”'`]?([\p{L}\p{N}_-]+)/iu,
  )
  const candidate = cleanName(match?.[1])
  return candidate && !/^(for|on|in|to|with)$/i.test(candidate)
    ? candidate
    : null
}

function patchAction(
  patches: Array<Record<string, unknown>>,
  extraPayload: Record<string, unknown> = {},
): CliAction {
  return { type: 'applyAppPatch', payload: { patches, ...extraPayload } }
}

function buildPageBlock(pageId: string, title: string): Record<string, unknown> {
  const subtitle = `Created from the conversation for ${title}.`
  return {
    meta: { name: `${title} Screen`, blockType: 'page-section' },
    state: {},
    data: {},
    events: {},
    flow: {},
    ui: {
      root: {
        id: `${pageId}-root`,
        type: 'container',
        className: 'min-h-screen bg-background p-6',
        children: [
          {
            id: `${pageId}-panel`,
            type: 'container',
            className: 'mx-auto flex max-w-md flex-col gap-4 rounded-2xl border border-border bg-card p-6 shadow-lg',
            children: [
              {
                id: `${pageId}-eyebrow`,
                type: 'text',
                text: 'CREATED FROM CHAT',
                className: 'text-xs font-semibold uppercase tracking-wide text-muted-foreground',
              },
              {
                id: `${pageId}-title`,
                type: 'text',
                text: title,
                className: 'text-3xl font-bold text-foreground',
              },
              {
                id: `${pageId}-subtitle`,
                type: 'text',
                text: subtitle,
                className: 'text-sm leading-6 text-muted-foreground',
              },
              {
                id: `${pageId}-cta`,
                type: 'button',
                text: 'Continue',
                className: 'mt-2 h-11 rounded-xl border border-border bg-foreground px-4 text-sm font-semibold text-background',
              },
            ],
          },
        ],
      },
    },
  }
}

function resolvePrimaryBlockName(project: LoadedApp, currentPageId?: string | null): string | null {
  const pages = project.manifest?.pages ?? []
  const activePage = currentPageId ? pages.find((page) => page.id === currentPageId) : null
  const activeBlock = Array.isArray(activePage?.blocks) ? activePage.blocks[0] : undefined
  if (typeof activeBlock === 'string' && project.blocks[activeBlock]) return activeBlock

  const firstPage = pages[0]
  const firstBlock = Array.isArray(firstPage?.blocks) ? firstPage.blocks[0] : undefined
  if (typeof firstBlock === 'string' && project.blocks[firstBlock]) return firstBlock
  return Object.keys(project.blocks ?? {})[0] ?? null
}

/**
 * F7 — the reply's language comes from the UI locale.
 *
 * This used to be `clarification(content, english, korean)` and chose between
 * the two by testing the USER'S OWN MESSAGE for Hangul. It is not possible for
 * that to be right — a Korean user writing an English request is not asking
 * for an English UI — and it was measurably wrong in both directions (see the
 * module note in `live-cogo-spec-intent-copy.ts`).
 *
 * The `content` parameter is gone on purpose rather than merely unused:
 * leaving it would let the sniff be reintroduced without changing a signature.
 */
function clarification(
  key: SpecIntentClarificationKey,
  locale: LocaleCode,
  params?: SpecIntentClarificationParams,
): SingleActionIntentResponse {
  return { text: specIntentClarificationText(key, locale, params) }
}

function extractVariableType(content: string): string | null {
  if (/\b(boolean|bool)\b|불리언|참거짓/i.test(content)) return 'boolean'
  if (/\b(number|integer|count)\b|숫자|카운트/i.test(content)) return 'number'
  if (/\b(array|list)\b|배열|목록/i.test(content)) return 'array'
  if (/\bobject\b|객체/i.test(content)) return 'object'
  if (/\bstring\b|문자열/i.test(content)) return 'string'
  return null
}

function extractEventType(content: string): string | null {
  const event = content.match(/\b(click|change|submit|input|hover|tap|focus|blur)\b/i)?.[1]
  if (event) return event.toLowerCase()
  if (/클릭|탭/i.test(content)) return 'click'
  if (/변경/i.test(content)) return 'change'
  if (/제출/i.test(content)) return 'submit'
  if (/입력/i.test(content)) return 'input'
  return null
}

function extractRenameTarget(content: string): string | null {
  const quoted = extractQuotedText(content)
  if (quoted) return slugify(quoted)
  const match = content.match(
    /(?:rename(?:\s+(?:it|this|flow))?\s+to|name\s+to|이름(?:을|를)?\s*(?:to|로|으로)?\s*(?:변경|바꿔|바꾸고)?|명칭(?:을|를)?\s*(?:로|으로)?\s*변경)\s*([\p{L}\p{N}_-]+)/iu,
  )
  return cleanName(match?.[1])
}

function extractQuotedText(content: string): string | null {
  return extractQuotedTexts(content)[0] ?? null
}

function extractQuotedTexts(content: string): string[] {
  return Array.from(
    content.matchAll(/"([^"]+)"|“([^”]+)”|'([^']+)'|`([^`]+)`|「([^」]+)」|『([^』]+)』/g),
    (match) => match.slice(1).find((value) => value !== undefined)?.trim() ?? '',
  ).filter(Boolean)
}

function extractExactTextReplacement(
  content: string,
): { sourceText: string; replacementText: string } | null {
  const quoted = extractQuotedTexts(content)
  if (quoted.length < 2) return null
  const sourceText = quoted[0]
  const replacementText = quoted.at(-1)
  return sourceText && replacementText
    ? { sourceText, replacementText }
    : null
}

function extractReplacementTextFollowingSource(
  content: string,
  sourceText: string,
): string | null {
  const sourceIndex = content.indexOf(sourceText)
  if (sourceIndex < 0) return null
  const suffix = content.slice(sourceIndex + sourceText.length)

  const korean = suffix.match(
    /^\s*(?:(?:문구|텍스트|라벨|제목)(?:을|를)?)?\s*(.+?)(?:으로|로)\s*(?:변경|수정|교체|바꿔|바꾸기)(?:해\s*줘|해\s*주세요|해주세요|해줘|줘)?\s*[.!?。]*$/iu,
  )?.[1]
  if (korean) return trimCommandPunctuation(korean)

  const english = suffix.match(
    /^\s*(?:(?:text|label|copy|headline|title)\s+)?(?:to|with)\s+(.+?)(?:\s+please)?\s*[.!?]*$/iu,
  )?.[1]
  return trimCommandPunctuation(english)
}

function extractTextContent(content: string): string | null {
  const quoted = extractQuotedText(content)
  if (quoted) return quoted

  const trailing = extractLiteralText(content)
  return trailing && !isGenericTextTarget(trailing) ? trailing : null
}

/**
 * F7's sibling, which the sweep did not name: the SAME Hangul sniff, a second
 * time, on the "which words?" clarification. (A third was on the document-page
 * success text.)
 */
function buildTextClarificationIntent(
  locale: LocaleCode,
  action: 'add' | 'update',
): SingleActionIntentResponse {
  return clarification(action === 'update' ? 'text-to-update' : 'text-to-add', locale)
}

function extractLiteralText(content: string): string | null {
  const korean = content.match(
    /(?:문구|텍스트|라벨|제목)(?:을|를)?\s*(.+?)(?:으로|로)\s*(?:변경|수정|교체|바꿔|바꾸기)\s*[.!?。]*$/iu,
  )?.[1]
  if (korean) return trimCommandPunctuation(korean)

  const english = content.match(
    /(?:text|label|copy|headline|title)\s*(?:called|named|as|to|:)\s*(.+?)\s*$/iu,
  )?.[1]
  return trimCommandPunctuation(english)
}

function trimCommandPunctuation(value: string | undefined): string | null {
  if (!value) return null
  const trimmed = value.trim().replace(/[.!?。]+$/u, '').trim()
  return trimmed || null
}

function resolveUiTarget(content: string): StudioTargetResolution | null {
  const english = content.match(
    /\b(?:selected\s+|the\s+)?([a-z][a-z0-9_-]*)\s+(?:text|label|copy|headline|title)\b/i,
  )?.[1]
  if (english) return resolveStudioTargetTerm(english)

  const korean = content.match(
    /(?:선택한\s+)?([\p{L}\p{N}_-]+)\s+(?:문구|텍스트|라벨|제목)/u,
  )?.[1]
  return korean ? resolveStudioTargetTerm(korean) : null
}

function extractNameAfter(content: string, marker: RegExp): string | null {
  const quoted = extractQuotedText(content)
  if (quoted) return slugify(quoted)
  const cleaned = content
    .replace(marker, ' ')
    .replace(/https?:\/\/[^\s]+/gi, ' ')
    .replace(/add|create|insert|connect|show|display|update|change|replace|remove|delete/gi, ' ')
    .replace(/\b(variable|state|local\s*state|datasource|data\s*source|api|endpoint|actionflow|action\s*flow|flow)\b/gi, ' ')
    .replace(/변수|상태|데이터소스|데이터\s*소스|엔드포인트|액션플로우|플로우/g, ' ')
    .replace(/추가|생성|삽입|연결|표시|보여|변경|수정|교체|삭제|제거/g, ' ')
    .replace(/boolean|bool|number|integer|count|array|list|string|url|method|rest|get|post/gi, ' ')
    .replace(/불리언|참거짓|숫자|카운트|배열|목록/g, ' ')
  return cleanName(cleaned)
}

function extractPageTitle(content: string): string | null {
  const quoted = extractQuotedText(content)
  if (quoted) return titleCaseWords(quoted)
  const cleaned = content
    .replace(PAGE_WORDS, ' ')
    .replace(/add|create|insert|show|display|new|추가|생성|삽입|표시|보여|새로운|새/g, ' ')
    .replace(/[^\p{L}\p{N}_ -]/gu, ' ')
    .trim()
    .split(/\s+/)
    .slice(0, 5)
    .join(' ')
  return cleaned ? titleCaseWords(cleaned) : null
}

function extractDocumentPageTitle(content: string): string | null {
  const quoted = extractQuotedText(content)
  if (quoted) return titleCaseWords(quoted)

  const koreanNamedTitle = content.match(
    /(?:문서(?:에|의)?\s*)?([\p{L}\p{N}][\p{L}\p{N}\s_-]{0,80}?)\s*(?:이라는|라는)\s*(?:제목|이름)(?:의)?\s*(?:새\s*)?(?:페이지|page|screen|slide)/iu,
  )?.[1]
  if (koreanNamedTitle) {
    const title = koreanNamedTitle
      .replace(/^(?:문서|document)(?:에|의)?\s*/i, '')
      .trim()
    if (title) return titleCaseWords(title)
  }

  const englishNamedTitle = content.match(
    /(?:create|add|insert)\s+(?:a\s+)?(?:new\s+)?(?:document\s+)?(?:page|screen|slide)\s+(?:titled|named|called)\s+([^,.。]+?)(?:\s+(?:please|now))?$/i,
  )?.[1]
  return englishNamedTitle ? titleCaseWords(englishNamedTitle.trim()) : null
}

function cleanName(value: string | undefined): string | null {
  if (!value) return null
  const cleaned = value
    .replace(/https?:\/\/[^\s]+/gi, '')
    .replace(/\b(add|create|insert|connect|show|display|update|change|replace|remove|delete|추가|생성|삽입|연결|표시|보여|변경|수정|교체|삭제|제거)\b/gi, '')
    .replace(/[^\p{L}\p{N}_ -]/gu, ' ')
    .trim()
    .split(/\s+/)
    .slice(0, 4)
    .join(' ')
  return cleaned ? slugify(cleaned) : null
}

function isGenericTextTarget(value: string): boolean {
  return /^(?:the\s*)?(?:page|screen|view|app|canvas|workspace|workingPage|workingScreen)$/i.test(value) ||
    /^(?:이)?(?:페이지|페이지에|화면|화면에|앱|앱에|작업화면|작업화면에)$/i.test(value)
}

function isGenericCommandTarget(value: string): boolean {
  return /^(?:for)?(?:this|the)?(?:page|screen|view|app|canvas|workspace|workingPage|workingScreen)$/i.test(value) ||
    /^(?:이)?(?:페이지|페이지에|화면|화면에|앱|앱에|작업화면|작업화면에)$/i.test(value)
}

function slugify(value: string): string {
  const cleaned = value
    .normalize('NFKD')
    .replace(/[^\p{L}\p{N}_ -]/gu, '')
    .trim()
  if (!cleaned) return ''
  const parts = cleaned.split(/[\s_-]+/).filter(Boolean)
  const [first = '', ...rest] = parts
  return [
    lowerFirst(first),
    ...rest.map((part) => upperFirst(part)),
  ].join('')
}

function kebabCase(value: string): string {
  return value
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/[\s_]+/g, '-')
    .toLowerCase()
}

function titleCaseWords(value: string): string {
  return value
    .trim()
    .split(/\s+/)
    .map((word) => /^[A-Z0-9]+$/.test(word) ? word : upperFirst(lowerFirst(word)))
    .join(' ')
}

function uniqueId(base: string, existing: Set<string>): string {
  let candidate = base
  let counter = 2
  while (existing.has(candidate)) {
    candidate = `${base}${counter}`
    counter += 1
  }
  return candidate
}

function lowerFirst(value: string): string {
  if (!value) return ''
  return /^[A-Z0-9]+$/.test(value) ? value.toLowerCase() : value.charAt(0).toLowerCase() + value.slice(1)
}

function upperFirst(value: string): string {
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : ''
}

function escapePointerPart(value: string): string {
  return value.replace(/~/g, '~0').replace(/\//g, '~1')
}
